import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import toast, { Toaster } from "react-hot-toast";

import { useCreateAppointment } from "~/api/appointments";
import { getProviders } from "~/api/providers";
import type { Provider } from "~/api/providers";
import { ROUTES } from "~/router/routes";
import CalendarSelector from "~/shared/components/ui/CalendarSelector";
import LocationDropdown from "~/shared/components/ui/LocationDropdown";
import ProviderCard from "~/shared/components/ui/ProviderCard";
import SpecialtyDropdown from "~/shared/components/ui/SpecialtyDropdown";
import { useCalendarDays } from "~/shared/hooks/useCalendarDays";
import { useDraftSync } from "~/shared/hooks/useDraftSync";
import { useNow } from "~/shared/hooks/useNow";
import {
  INSURANCE_PROVIDERS,
  NEW_YORK_STATE,
  US_STATES,
  VIDEO_CALL_LOCATION,
} from "~/shared/utils/constants";
import {
  countBookableSlots,
  currentDay,
  currentDayName,
  currentMonth,
  formatAppointmentDate,
  formatAppointmentTime,
  formatAppointmentTimeWithTimezone,
  isSlotInPast,
} from "~/shared/utils/dates";
import { buildProviderSearchUrl } from "~/shared/utils/providerSearchLink";
import type {
  ProviderLoadedInfo,
  ProvidersLoadedTrigger,
} from "~/shared/utils/types";
import { useSignUpStore } from "~/stores";
import { useFunnelStore } from "~/stores/useFunnelStore";
import { tw } from "~/utils";
import {
  getStaleSlotMessage,
  isExpiredTokenError,
  isStaleSlotError,
} from "../utils/bookingErrors";
import { isNonProduction } from "../utils/envUtils";
import { BACKOFFICE_FLOW_VERSION } from "../utils/flowVersion";
import { funnelTracker } from "../utils/funnelTracking";
import { getUtmParams } from "../utils/utmCapture";

// --- Main Screen ---

const AdminProviderSelectionScreen: React.FC = () => {
  const { patient, setPatientField, setAppointmentBooked } = useSignUpStore();
  const { syncDraft, finalizeDraft } = useDraftSync();
  const sessionId = useFunnelStore((s) => s.session_id);

  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(
    null,
  );
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [showConfirmed, setShowConfirmed] = useState(false);
  const [confirmedData, setConfirmedData] = useState<{
    providerName: string;
    avatarUrl: string | null;
    dateTime: string;
    location: string | null;
  } | null>(null);

  // Tracking refs for trigger detection
  const prevDateRef = useRef<string | null>(null);
  const prevConditionsRef = useRef<string[]>([]);
  const prevLocationRef = useRef<string>("");
  const isInitialLoadRef = useRef(true);

  // Filters panel — starts expanded, auto-collapses after first data load
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  // The panel clips its content (overflow-hidden) so the max-height collapse
  // animation reads cleanly. Once fully expanded and idle we let it overflow,
  // otherwise the Location dropdown's popover is cut off at the panel edge.
  const [filtersAnimating, setFiltersAnimating] = useState(false);
  const hasAutoCollapsedRef = useRef(false);

  // Seed local filter state from the patient once, then let staff drive it.
  const hasInitializedRef = useRef(false);

  // Set backoffice flow version and start provider_selection step
  useEffect(() => {
    funnelTracker.setFlowVersion(BACKOFFICE_FLOW_VERSION);
    funnelTracker.startStep("provider_selection");
    return () => {
      funnelTracker.clearFlowVersion();
    };
  }, []);

  useEffect(() => {
    if (patient && !hasInitializedRef.current) {
      setSelectedConditions(patient.conditions);
      setSelectedLocation(patient.preferredOfficeVisit ?? VIDEO_CALL_LOCATION);
      hasInitializedRef.current = true;
    }
  }, [patient]);

  // Sync condition filter changes back to store and draft
  const isInitialSyncRef = useRef(true);
  useEffect(() => {
    if (isInitialSyncRef.current) {
      isInitialSyncRef.current = false;
      return;
    }
    setPatientField("conditions", selectedConditions);
    syncDraft();
  }, [selectedConditions]);

  // Sync location filter changes back to store and draft
  const isInitialLocationSyncRef = useRef(true);
  useEffect(() => {
    if (isInitialLocationSyncRef.current) {
      isInitialLocationSyncRef.current = false;
      return;
    }
    const isTelehealth = selectedLocation === VIDEO_CALL_LOCATION;
    setPatientField("visitType", isTelehealth ? "Telehealth" : "In-Person");
    setPatientField("preferredOfficeVisit", selectedLocation);
    syncDraft();
  }, [selectedLocation]);

  const handleDateSelection = useCallback((date: Date | null) => {
    setSelectedDate(date);
  }, []);

  const {
    calendarDays,
    canNavigatePrev,
    navigatePrevWeek,
    navigateNextWeek,
    isLoading: isCalendarLoading,
    error: calendarError,
    availabilityForNextMonth,
    isMobile,
  } = useCalendarDays(
    selectedConditions,
    selectedLocation,
    handleDateSelection,
    // Staff waive the patient funnel's minimum-notice buffer. Passed here as well
    // as on the provider query below so the day badges and the slot list agree.
    true,
  );

  const queryClient = useQueryClient();
  const createAppointmentMutation = useCreateAppointment();

  // The search above waives the minimum-notice buffer for staff, but never the
  // past cutoff, so ProviderCard still drops a slot here once its time passes.
  // Clear the selection with it, or Confirm stays enabled on a time booking will
  // refuse.
  const now = useNow();

  useEffect(() => {
    if (createAppointmentMutation.isPending || showConfirmed) {
      return;
    }

    if (selectedTime !== null && isSlotInPast(selectedTime, now)) {
      setSelectedTime(null);
      setSelectedProvider(null);
      toast.error("That time has already passed — please pick another slot.");
    }
  }, [now, selectedTime, createAppointmentMutation.isPending, showConfirmed]);

  const {
    data: providers = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "providers",
      selectedConditions,
      selectedLocation,
      selectedDate?.toISOString().split("T")[0],
      // The referring practice narrows the roster, so it must split the cache.
      patient.referringPracticeId ?? null,
    ],
    queryFn: () => {
      return getProviders({
        location: selectedLocation,
        startDate: selectedDate?.toISOString().split("T")[0] ?? "",
        page: 1,
        perPage: 10,
        state: US_STATES.find((state) => state.label === patient.state)?.value,
        primaryInsurance:
          INSURANCE_PROVIDERS[
            patient.primaryInsurance
              .providerName as keyof typeof INSURANCE_PROVIDERS
          ],
        secondaryInsurance: patient.secondaryInsurance?.providerName
          ? INSURANCE_PROVIDERS[
              patient.secondaryInsurance
                .providerName as keyof typeof INSURANCE_PROVIDERS
            ]
          : undefined,
        patientEmail: patient.email,
        patientId: patient.id,
        conditions: selectedConditions,
        // Booking a patient in half an hour is a normal front-desk action, so
        // staff waive the funnel's minimum-notice buffer. They do NOT waive the
        // already-started cutoff — StoreAppointmentAction enforces that for
        // every caller and it cannot be turned off from the client.
        staffBooking: true,
        referringPracticeId: patient.referringPracticeId ?? undefined,
      });
    },
    enabled: selectedConditions.length > 0 && !!selectedDate,
    // Keep the previously loaded providers on screen while a new date/filter is
    // refetching — mirrors ProviderSelectionScreen's placeholderData usage so
    // the list doesn't collapse mid-interaction.
    placeholderData: (previousData) => previousData,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  // Auto-collapse filters after first providers load
  useEffect(() => {
    if (!hasAutoCollapsedRef.current && providers.length > 0) {
      hasAutoCollapsedRef.current = true;
      setFiltersExpanded(false);
    }
  }, [providers.length]);

  const handleCalendarDateSelect = useCallback(
    (date: Date) => {
      const newDateStr = date.toISOString().split("T")[0];
      const prevDateStr = selectedDate?.toISOString().split("T")[0];
      const appointmentType =
        selectedLocation === VIDEO_CALL_LOCATION ? "telehealth" : "in-person";

      funnelTracker.trackCalendarDateSelect(
        newDateStr,
        prevDateStr,
        appointmentType as "telehealth" | "in-person",
      );
      prevDateRef.current = prevDateStr ?? null;

      setSelectedDate(date);
      setSelectedProvider(null);
      setSelectedTime(null);
    },
    [selectedDate, selectedLocation],
  );

  const handleSpecialtyChange = useCallback(
    (specialties: string[]) => {
      prevConditionsRef.current = [...selectedConditions];
      funnelTracker.trackFilterChange("specialty", specialties);
      setSelectedConditions(specialties);
      setSelectedProvider(null);
      setSelectedTime(null);
    },
    [selectedConditions],
  );

  const handleLocationChange = useCallback(
    (location: string) => {
      prevLocationRef.current = selectedLocation;
      funnelTracker.trackFilterChange("location", location);
      setSelectedLocation(location);
      setSelectedProvider(null);
      setSelectedTime(null);
    },
    [selectedLocation],
  );

  const handleTimeSelect = useCallback((provider: Provider, time: string) => {
    funnelTracker.trackTimeSlotSelect(
      provider.id,
      provider.fullName,
      time,
      countBookableSlots(provider.availableSlots),
    );
    setSelectedProvider(provider);
    setSelectedTime(time);
  }, []);

  // Calendar navigation with tracking
  const handleNavigatePrev = useCallback(() => {
    funnelTracker.trackCalendarNavigate("prev");
    navigatePrevWeek();
  }, [navigatePrevWeek]);

  const handleNavigateNext = useCallback(() => {
    funnelTracker.trackCalendarNavigate("next");
    navigateNextWeek();
  }, [navigateNextWeek]);

  // Track providers loaded with trigger detection
  useEffect(() => {
    if (!selectedDate || isLoading) return;

    const currentDateStr = selectedDate.toISOString().split("T")[0];
    const appointmentType: "telehealth" | "in-person" =
      selectedLocation === VIDEO_CALL_LOCATION ? "telehealth" : "in-person";

    const providerInfo: ProviderLoadedInfo[] = providers.map((p) => ({
      id: p.id,
      name: p.fullName,
      slots: countBookableSlots(p.availableSlots),
    }));

    const filters = {
      specialties: selectedConditions,
      location: selectedLocation,
      date: currentDateStr,
      appointment_type: appointmentType,
    };

    let trigger: ProvidersLoadedTrigger;
    if (isInitialLoadRef.current) {
      trigger = { type: "initial_load" };
      isInitialLoadRef.current = false;
    } else if (prevDateRef.current && prevDateRef.current !== currentDateStr) {
      trigger = {
        type: "date_change",
        previous_date: prevDateRef.current,
        new_date: currentDateStr,
      };
    } else if (
      prevConditionsRef.current.length > 0 &&
      JSON.stringify(prevConditionsRef.current) !==
        JSON.stringify(selectedConditions)
    ) {
      trigger = {
        type: "filter_change",
        filter_type: "specialty",
        previous_value: prevConditionsRef.current,
        new_value: selectedConditions,
      };
    } else if (
      prevLocationRef.current &&
      prevLocationRef.current !== selectedLocation
    ) {
      trigger = {
        type: "filter_change",
        filter_type: "location",
        previous_value: prevLocationRef.current,
        new_value: selectedLocation,
      };
    } else {
      trigger = { type: "initial_load" };
    }

    funnelTracker.trackProvidersLoaded(providerInfo, filters, trigger);

    prevDateRef.current = currentDateStr;
    prevConditionsRef.current = [...selectedConditions];
    prevLocationRef.current = selectedLocation;
  }, [
    providers,
    selectedDate,
    selectedConditions,
    selectedLocation,
    isLoading,
  ]);

  // One-shot guard so a booking token that is rejected even after we re-issue it
  // can't spin the recover -> retry -> recover loop. Reset once the staff member
  // picks a different slot.
  const tokenRecoveryAttemptedRef = useRef(false);

  // True while recoverTokenAndBook is awaiting finalizeDraft(). Guards against a
  // second CONFIRM click during that round-trip tripping the one-shot guard and
  // restarting the (finalized) session — parity with the patient screen.
  const [isRecovering, setIsRecovering] = useState(false);

  // Wipe the stale sign-up session and send staff back to a fresh
  // /backoffice/sign-up. Only used as a last resort — when we cannot re-issue a
  // booking token from the draft — matching the fresh-session restart staff
  // already do via "book another".
  const restartExpiredSession = () => {
    toast.error(
      "This sign-up session has expired. Please start a new sign-up to book.",
    );
    sessionStorage.removeItem("signup-form-storage");
    useSignUpStore.getState().reset();
    useFunnelStore.getState().resetSession();
    window.location.href = ROUTES.adminSignUp;
  };

  // The booking token is short-lived and can be missing (a session that predates
  // it) or expired server-side. Restarting the whole sign-up would send staff
  // back to the email step, where the email-exists guard permanently blocks the
  // patient — they already exist in Healthie from finalize. So instead re-issue a
  // token by re-finalizing the still-valid draft and retry the booking. Only fall
  // back to a restart if that recovery itself fails.
  const recoverTokenAndBook = async () => {
    if (tokenRecoveryAttemptedRef.current) {
      restartExpiredSession();

      return;
    }
    tokenRecoveryAttemptedRef.current = true;
    setIsRecovering(true);

    try {
      const result = await finalizeDraft();

      if (!result.bookingToken) {
        restartExpiredSession();

        return;
      }

      setPatientField("bookingToken", result.bookingToken);
      submitBooking(result.bookingToken);
    } catch {
      restartExpiredSession();
    } finally {
      setIsRecovering(false);
    }
  };

  const submitBooking = (bookingToken: string) => {
    if (!selectedProvider || !selectedDate || !selectedTime) return;

    createAppointmentMutation.mutate(
      {
        providerId: selectedProvider.id,
        dateTime: selectedTime,
        preferredOfficeVisit: selectedLocation ?? patient.preferredOfficeVisit,
        bookingToken,
        conditions: selectedConditions,
        // Admin-specific: which of the staff-selected conditions this provider
        // actually specializes in, for downstream reporting.
        matchedSpecialties: selectedProvider.metadata.specialties.filter((s) =>
          selectedConditions.includes(s),
        ),
        utmParams: patient.utmParams ?? getUtmParams(),
        sendConversionEvent: false,
      },
      {
        onSuccess: (data) => {
          const appointmentType: "telehealth" | "in-person" =
            selectedLocation === VIDEO_CALL_LOCATION
              ? "telehealth"
              : "in-person";

          funnelTracker.trackFunnelComplete({
            provider_id: selectedProvider.id,
            provider_name: selectedProvider.fullName,
            appointment_date: selectedDate.toISOString().split("T")[0],
            appointment_time: selectedTime,
            appointment_type: appointmentType,
            location: data.location || selectedLocation || "",
            specialties: selectedConditions,
            providers_shown: providers.length,
            slots_available: countBookableSlots(
              selectedProvider.availableSlots,
            ),
          });

          setAppointmentBooked(true);
          setConfirmedData({
            providerName: selectedProvider.fullName,
            avatarUrl: selectedProvider.avatarUrl,
            dateTime: selectedTime,
            location: data.location,
          });
          setShowConfirmed(true);
        },
        onError: (error: any) => {
          // Backend rejected the booking token (expired/tampered). Try once to
          // silently re-issue a fresh token from the still-valid draft and retry;
          // only restart if that fails.
          if (isExpiredTokenError(error)) {
            void recoverTokenAndBook();
            return;
          }

          if (isStaleSlotError(error)) {
            // The slot was taken between our availability check and this booking
            // — covers both no_availability_found (our re-check) and
            // slot_unavailable (Healthie refused the create). Refresh availability
            // data and clear the stale selection so staff pick again, instead of
            // a generic error that leaves the taken slot selected.
            tokenRecoveryAttemptedRef.current = false;
            queryClient.invalidateQueries({ queryKey: ["providers"] });
            setSelectedTime(null);
            setSelectedProvider(null);
            // slot_unavailable now covers BOTH the double-booking race and the
            // past-slot refusal. Staff waive the notice buffer but not that floor,
            // so they can reach it too — judge the wording by the slot's own time
            // rather than blaming another patient for the clock.
            toast.error(getStaleSlotMessage(selectedTime));
          } else {
            toast.error(
              error?.response?.data?.error?.message || "Something went wrong",
            );
          }
        },
      },
    );
  };

  const handleConfirm = () => {
    if (!selectedProvider || !selectedDate || !selectedTime) return;

    // A recovery finalize is already in flight; ignore the extra click.
    if (isRecovering) return;

    // The signed booking token is the sole proof this session owns the patient.
    // If it's absent (never issued or dropped from storage) recover one from the
    // draft rather than sending an empty token the backend would reject.
    const { bookingToken } = patient;
    if (!bookingToken) {
      void recoverTokenAndBook();
      return;
    }

    submitBooking(bookingToken);
  };

  const providerSearchUrl = buildProviderSearchUrl({
    conditions: selectedConditions,
    state: US_STATES.find((s) => s.label === patient.state)?.value ?? "",
    office: selectedLocation,
    groupName: patient.groupName ?? "",
  });

  // Show confirmation screen after successful booking
  if (showConfirmed && confirmedData) {
    return (
      <AdminAppointmentConfirmedInline
        providerName={confirmedData.providerName}
        providerAvatarUrl={confirmedData.avatarUrl}
        appointmentDateTime={confirmedData.dateTime}
        location={confirmedData.location}
      />
    );
  }

  return (
    <>
      <Toaster containerClassName="mt-16" />
      <div className="flex h-screen flex-col items-center bg-pink-100 sm:min-h-screen sm:py-8">
        {/* Main container */}
        <div className="flex h-full w-full max-w-[570px] flex-col overflow-hidden bg-white shadow-xl sm:h-auto sm:max-h-[calc(100vh-64px)] sm:rounded-lg">
          {/* Compact header with collapsible filters */}
          <div className="shrink-0 border-b border-gray-100 bg-white px-4 pt-3 sm:px-6 sm:pt-4">
            <h1 className="font-display mb-2 text-base sm:text-lg">
              Select a provider
            </h1>

            {/* Summary bar — always visible, tap to expand filters */}
            <button
              type="button"
              onClick={() => {
                setFiltersAnimating(true);
                setFiltersExpanded((v) => !v);
              }}
              className="mb-2 flex w-full items-center justify-between"
            >
              <span className="text-sm text-gray-500">
                Search Preferences
                {selectedLocation && (
                  <>
                    {" : "}
                    <span className="font-medium text-gray-700">
                      {selectedLocation === VIDEO_CALL_LOCATION
                        ? "Telehealth"
                        : selectedLocation}
                    </span>
                  </>
                )}
                {" : "}
                <span className="font-medium text-gray-700">
                  {selectedConditions.length} Health Condition
                  {selectedConditions.length !== 1 ? "s" : ""}
                </span>
              </span>
              <svg
                className={tw(
                  "size-4 text-gray-400 transition-transform",
                  filtersExpanded && "rotate-180",
                )}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            <a
              href={providerSearchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mb-2 inline-block text-xs font-medium text-green-700 underline"
            >
              View ranking for this search
            </a>

            {/* Expandable filters panel with smooth transition */}
            <div
              onTransitionEnd={(e) => {
                if (e.propertyName === "max-height") {
                  setFiltersAnimating(false);
                }
              }}
              className={tw(
                "grid grid-cols-2 gap-3 transition-all duration-300 ease-in-out",
                // Clip while collapsed or mid-animation; once expanded and idle,
                // let the Location dropdown popover overflow instead of clipping.
                filtersExpanded && !filtersAnimating
                  ? "overflow-visible"
                  : "overflow-hidden",
                filtersExpanded
                  ? "mb-3 max-h-[300px] opacity-100"
                  : "max-h-0 opacity-0",
              )}
            >
              {/* Conditions */}
              <div
                className={tw(
                  "flex flex-col",
                  patient.state !== NEW_YORK_STATE && "col-span-2",
                )}
              >
                <label className="mb-1 block text-[11px] font-medium text-gray-400">
                  Health conditions
                </label>
                <SpecialtyDropdown
                  selectedSpecialties={selectedConditions}
                  onSpecialtyChange={handleSpecialtyChange}
                />
              </div>

              {/* Location (NY only) — single office/telehealth, same as the
                  patient flow's LocationDropdown */}
              {patient.state === NEW_YORK_STATE && (
                <div className="flex flex-col">
                  <label className="mb-1 block text-[11px] font-medium text-gray-400">
                    Location preference
                  </label>
                  <LocationDropdown
                    selectedLocation={selectedLocation}
                    onLocationChange={handleLocationChange}
                  />
                </div>
              )}
            </div>

            {/* Calendar — always visible */}
            <div className="pb-3">
              <CalendarSelector
                month={currentMonth}
                day={currentDay}
                dayName={currentDayName}
                daysInView={calendarDays}
                onDateSelect={handleCalendarDateSelect}
                onNavigatePrev={canNavigatePrev ? handleNavigatePrev : null}
                onNavigateNext={handleNavigateNext}
                selectedDate={selectedDate}
                loading={isCalendarLoading}
                isMobile={isMobile}
              />
            </div>
          </div>

          {/* Scrollable providers list */}
          <div
            className="min-h-0 flex-1 overflow-y-auto bg-gray-50 px-4 py-4 sm:px-6"
            onScroll={(e) => {
              if (
                filtersExpanded &&
                (e.target as HTMLDivElement).scrollTop > 50
              ) {
                setFiltersExpanded(false);
              }
            }}
          >
            {calendarError && (
              <div className="mb-4 text-xs text-red-500">
                Error loading calendar
              </div>
            )}

            {isLoading && (
              <div className="py-8 text-center text-sm text-gray-500">
                Loading providers...
              </div>
            )}

            {error && (
              <div className="mb-4 text-xs text-red-500">
                Error loading providers
              </div>
            )}

            {/* Provider cards */}
            <div className="flex flex-col gap-3">
              {providers.map((provider) => (
                <ProviderCard
                  key={provider.id}
                  provider={provider}
                  location={selectedLocation}
                  onTimeSelect={(time) => handleTimeSelect(provider, time)}
                  selectedTime={
                    selectedProvider?.id === provider.id ? selectedTime : null
                  }
                />
              ))}
            </div>

            {/* Results count */}
            {!isLoading && providers.length > 0 && (
              <div className="mt-4 text-center text-xs text-gray-400">
                {providers.length} provider{providers.length !== 1 ? "s" : ""}{" "}
                available
              </div>
            )}

            {/* No eligible provider for these conditions on this plan at all —
                not a date problem, so route to the team rather than "try another
                date". Distinct from the per-date empty state below. */}
            {!isCalendarLoading &&
              availabilityForNextMonth === false &&
              selectedConditions.length > 0 && (
                <div className="mb-4 rounded-lg bg-amber-50 p-3 text-center text-sm text-amber-800">
                  No eligible providers for {selectedConditions.join(", ")} on{" "}
                  {patient.groupName || "this plan"}.
                </div>
              )}

            {/* Eligible providers exist, but none on the selected date. */}
            {!isLoading &&
              !error &&
              availabilityForNextMonth !== false &&
              providers.length === 0 &&
              selectedDate &&
              selectedConditions.length > 0 && (
                <div className="py-8 text-center text-sm text-gray-500">
                  No providers are available for this date. Please choose
                  another date.
                </div>
              )}
          </div>

          {/* Sticky footer with real booking */}
          <div className="shrink-0 border-t border-gray-200 bg-green-200">
            {isNonProduction() && (sessionId || patient.groupName) && (
              <div className="border-b border-green-300 px-4 py-1 text-[10px] text-gray-500">
                {patient.groupName && <span>Group: {patient.groupName}</span>}
                {patient.groupName && sessionId && <span> &middot; </span>}
                {sessionId && <span>Session: {sessionId}</span>}
              </div>
            )}
            <div className="flex w-full items-center justify-between">
              <div className="flex-1 px-4">
                {(!selectedProvider || !selectedTime) && (
                  <span className="text-xs text-gray-500">
                    Select a date and time slot to continue
                  </span>
                )}
                {selectedProvider && selectedTime && (
                  <span className="text-xs text-gray-700">
                    {selectedProvider.fullName} &middot;{" "}
                    {formatAppointmentTime(selectedTime)} &middot;{" "}
                    {selectedLocation === VIDEO_CALL_LOCATION
                      ? "Telehealth"
                      : selectedLocation}
                  </span>
                )}
              </div>
              <button
                type="button"
                disabled={
                  !selectedProvider ||
                  !selectedDate ||
                  !selectedTime ||
                  createAppointmentMutation.isPending ||
                  isRecovering
                }
                onClick={handleConfirm}
                className={tw(
                  "flex items-center gap-2 px-6 py-4 font-medium",
                  selectedProvider && selectedDate && selectedTime
                    ? "cursor-pointer bg-green-300"
                    : "cursor-not-allowed bg-gray-300 opacity-50",
                )}
              >
                {createAppointmentMutation.isPending || isRecovering
                  ? "Booking..."
                  : "CONFIRM"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Inline confirmation to avoid separate route (exported for tests)
export const AdminAppointmentConfirmedInline: React.FC<{
  providerName: string;
  providerAvatarUrl: string | null;
  appointmentDateTime: string;
  location: string | null;
}> = ({ providerName, providerAvatarUrl, appointmentDateTime, location }) => {
  const formattedDate = formatAppointmentDate(appointmentDateTime);
  const formattedTime = formatAppointmentTimeWithTimezone(appointmentDateTime);
  const firstName = providerName.split(" ")[0];

  const getAddress = (loc: string): string => {
    switch (loc) {
      case "In person at Buffalo":
        return "5360 Genesee Street, Lancaster, NY 14026";
      case "In person at Albany":
        return "125 Wolf Road Suite 212 Albany, NY 12205";
      case "In person at Rochester":
        return "1160K Pittsford-Victor Road, 2nd Floor #4, Pittsford, NY 14534";
      case "In person at Syracuse":
        return "5100 West Genesee Street Camillus NY, 13031";
      default:
        return "Online";
    }
  };

  const handleBookAnother = () => {
    sessionStorage.removeItem("signup-form-storage");
    useSignUpStore.getState().reset();
    useFunnelStore.getState().resetSession();
    window.location.href = ROUTES.adminSignUp;
  };

  return (
    <div className="flex h-screen flex-col items-center bg-pink-100 sm:min-h-screen sm:py-8">
      <div className="flex h-full w-full max-w-[570px] flex-col items-center justify-center overflow-hidden bg-white p-6 shadow-xl sm:h-auto sm:rounded-lg">
        <h1 className="font-display mb-2 text-xl">
          Appointment confirmed with {firstName}!
        </h1>
        <p className="mb-6 text-center text-sm text-gray-400">
          The patient will receive an email with their appointment details.
        </p>
        <div className="mb-6 w-full max-w-sm rounded-xl border border-gray-200 bg-gray-50/50 p-5">
          <div className="mb-4 flex flex-col items-center">
            {providerAvatarUrl ? (
              <img
                src={providerAvatarUrl}
                alt={providerName}
                className="mb-2 size-14 rounded-full object-cover"
              />
            ) : (
              <div className="mb-2 flex size-14 items-center justify-center rounded-full bg-green-100 text-green-700">
                <span className="font-display text-xl">
                  {providerName.charAt(0)}
                </span>
              </div>
            )}
            <h2 className="font-display text-lg text-gray-900">
              {providerName}
            </h2>
          </div>
          <div className="space-y-2 border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Date</span>
              <span className="text-sm font-medium text-gray-900">
                {formattedDate}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Time</span>
              <span className="text-sm font-medium text-gray-900">
                {formattedTime}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Location</span>
              <span className="text-sm font-medium text-gray-900">
                {location === "via Telehealth"
                  ? "Telehealth (Zoom)"
                  : getAddress(location ?? "")}
              </span>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={handleBookAnother}
          className="inline-flex items-center gap-2 rounded-lg bg-green-300 px-6 py-3 font-medium text-black hover:bg-green-200"
        >
          Book Another Patient
        </button>
      </div>
    </div>
  );
};

export default AdminProviderSelectionScreen;
